import { useState, useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import { Navigation } from "lucide-react";
import { Pool } from "@/types";
import { Button } from "./ui/button";
import { PoolRepository } from "@/infrastructure/repositories/PoolRepository";

interface FindPoolProps {
  onNavigate: (page: string) => void;
}

const PAGE_SIZE = 12;

function getDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function openGoogleMapUrl(rawUrl?: string) {
  if (!rawUrl) return;

  const url = rawUrl.startsWith("http://") || rawUrl.startsWith("https://")
    ? rawUrl
    : `https://${rawUrl}`;

  window.open(url, "_blank", "noopener,noreferrer");
}

export function FindPoolPage({ onNavigate }: FindPoolProps) {
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const poolRepo = new PoolRepository();

  const [pools, setPools] = useState<Pool[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  console.log(setSearchQuery);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  console.log(setUserLocation);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

    const navigate = (url: string, page: string, onNavigate: (p: string) => void) => {
      window.history.pushState({}, "", url);
      onNavigate(page);
    };

const toSlug = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");


  // ----------------------------------
  // FETCH POOLS (SERVER PAGINATION)
  // ----------------------------------
  useEffect(() => {
    const loadPools = async () => {
      if (loading || !hasMore) return;

      setLoading(true);

      const newPools = await poolRepo.getPoolList(page, PAGE_SIZE);

      setPools(prev => [...prev, ...newPools]);

      if (newPools.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setLoading(false);
    };

    loadPools();
  }, [page]);

  // ----------------------------------
  // INFINITE SCROLL
  // ----------------------------------
  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !loading) {
        setPage(prev => prev + 1);
      }
    });

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMore, loading]);

  // ----------------------------------
  // GET USER LOCATION
  // ----------------------------------
  // useEffect(() => {
  //   if (!navigator.geolocation) return;

  //   navigator.geolocation.getCurrentPosition(
  //     pos => {
  //       setUserLocation({
  //         lat: pos.coords.latitude,
  //         lng: pos.coords.longitude,
  //       });
  //     },
  //     () => setUserLocation(null)
  //   );
  // }, []);

  // ----------------------------------
  // FILTER + SORT
  // ----------------------------------
  const filteredPools = pools
    .filter(pool => {
      const q = searchQuery.toLowerCase();
      return (
        pool.poolname.toLowerCase().includes(q) ||
        pool.city?.toLowerCase().includes(q) ||
        pool.state?.toLowerCase().includes(q) ||
        pool.type?.toLowerCase().includes(q)
      );
    })
    .map(pool => ({
      ...pool,
      distance:
        userLocation && pool.lat && pool.lng
          ? getDistanceKm(
              userLocation.lat,
              userLocation.lng,
              pool.lat,
              pool.lng
            )
          : null,
    }))
    .sort((a, b) => {
      if (a.distance == null || b.distance == null) return 0;
      return a.distance - b.distance;
    });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-slate-900">
      <div className="flex-1">
        {/* HEADER */}
        <header className="border-b sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <img
              src={logoSrc}
              onClick={() => navigate("/" , "landing" , onNavigate)}
              alt="Swimnerd"
              className="h-12 cursor-pointer"
            />

            <div className="flex gap-4">
            <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool", onNavigate)}>
              Find Pool
            </Button>
            <Button variant="ghost" onClick={() => navigate("/blog", "blog", onNavigate)}>
              Blog
            </Button>
            <Button variant="ghost" onClick={() => navigate("/signin", "signin", onNavigate)}>
              Sign In
            </Button>
            <Button onClick={() => navigate("/signup", "signup", onNavigate)}>
              Get Started
            </Button>
          </div>
          </div>
        </header>

        {/* Hero */}
      <section className="relative overflow-hidden text-white">
  <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--primary)/0.85))]" />
  
  <div className="relative max-w-7xl mx-auto px-6 py-20">
    {/* <h1 className="text-sm uppercase tracking-wider text-white/80 mb-4">
      Find Pools
    </h1> */}

    <h2 className="text-5xl font-bold mb-6 max-w-3xl leading-tight">
      Discover Swimming Pools Near You
    </h2>

    <p className="text-lg text-white/90 max-w-2xl">
      Explore verified swimming pools, training facilities, and coaching centers.
      Find the right place to train, learn, and improve your performance.
    </p>
  </div>
</section>


        {/* SEARCH */}
        <div className="px-6 mt-6">
          {/* <div className="relative max-w-3xl mx-auto">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by pool name, city, or type"
              className="w-full rounded-xl px-4 py-3 pr-10 border bg-background"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div> */}
        </div>

        {/* GRID */}
        <main className="max-w-8xl mx-auto px-6 pt-8 pb-20">
          {filteredPools.length === 0 && !loading ? (
            <div className="flex items-center justify-center min-h-[40vh] text-muted-foreground text-lg">
              No swimming pools found
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {filteredPools.map(pool => (
                <div
                  key={pool.id}
                  onClick={() => {
                    const slug = toSlug(pool.poolname);
                    const url = `/pool/${pool.id}/${slug}`;

                    window.history.pushState({}, "", url);
                    onNavigate(`pool-${pool.id}-${slug}`);
                  }}
                  className="rounded-2xl overflow-hidden cursor-pointer bg-background border hover:shadow-xl transition flex flex-col"
                >
                  <div className="relative">
                    <img
                      src={pool.image}
                      alt={pool.poolname}
                      className="h-48 w-full object-cover"
                    />

                    {pool.googlemap_url && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          openGoogleMapUrl(pool.googlemap_url);
                        }}
                        className="absolute top-3 right-3 h-9 w-9 rounded-full bg-background border flex items-center justify-center"
                      >
                        <Navigation className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-start gap-2">
                      <h3
                        className="font-semibold flex-1 min-w-0 truncate"
                        title={pool.poolname}
                      >
                        {pool.poolname}
                      </h3>

                      {pool.is_verified && (
                        <span className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          Verified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {pool.city}, {pool.state}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <div
              ref={loadMoreRef}
              className="h-16 flex items-center justify-center"
            >
              {loading && (
                <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            
            </div>
              )}
            </div>
          )}
        </main>
      </div>

      <footer className="border-t bg-background text-center py-8 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Swimnerd. Built for smarter swimming.
      </footer>
    </div>
  );
}
